import React from 'react';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Autocomplete, {
  autocompleteClasses,
  AutocompleteProps,
  AutocompleteValue,
} from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';
import Input from './Input';
import { NATION_WIDE } from '@/constants/city';

export type MyAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'onChange' | 'options' | 'renderInput'
> & {
  value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
  onChange: (
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
  ) => void;
  placeholder?: string;
  label?: string;
  labelColor?: string;
  openAtFirstClick?: boolean;
  getOptions: (query: string) => Promise<{ data: any[] }>;
  inputSx?: React.CSSProperties | Record<string, React.CSSProperties>;
  error?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  subLabel?: string;
  isSearchCity?: boolean;
  handleMyInputChange?: () => void;
  disableUnderline?: boolean;
  clearValue?: boolean;
  clearOnBlur?: boolean;
  inputClassName?: string;
  searchAtFirstClick?: boolean;
};

const MyAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: MyAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [defaultOptions, setDefaultOptions] = React.useState<readonly T[]>([]);

  const {
    value = undefined,
    onChange,
    getOptions,
    getOptionLabel,
    freeSolo,
    placeholder = '',
    label = '',
    labelColor,
    className = '',
    openAtFirstClick = true,
    noOptionsText = '抱歉，暂未搜索到相关数据',
    sx,
    inputSx = {},
    disabled = false,
    error = false,
    helperText = '',
    subLabel,
    filterOptions = (options) => options,
    startAdornment,
    endAdornment = <SearchIcon sx={{ color: '#79747e' }} />,
    isSearchCity = false,
    handleMyInputChange,
    disableUnderline,
    clearValue,
    clearOnBlur = true,
    inputClassName,
    searchAtFirstClick = false,
    ...otherProps
  } = props;

  const styledPopper = styled(Popper)({
    paddingTop: '4px',
    [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}.${autocompleteClasses.focused}`]:
      {
        color: '#1861bf',
        background: '#eef1fe',
        borderRadius: '8px',
      },
  });

  const handleChange = (
    event: React.SyntheticEvent,
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
  ) => {
    onChange && onChange(value);
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    inputValue: string,
    reason: string,
  ) => {
    if (!openAtFirstClick && event) {
      setOpen(true);
    }
    setInputValue(inputValue);
    if (reason === 'input') {
      handleSearch(inputValue);
      handleMyInputChange?.();
    }
    if (freeSolo && event && onChange) {
      onChange(
        inputValue as AutocompleteValue<
          T,
          Multiple,
          DisableClearable,
          FreeSolo
        >,
      );
    }
  };

  const handleSearch = React.useCallback(
    debounce(async (query) => {
      setLoading(true);
      const response = await getOptions(query.trim());
      setLoading(false);
      const data = response.data;
      if (Array.isArray(data) && isSearchCity) {
        data.unshift(NATION_WIDE as any);
        setOptions(data);
      } else {
        setOptions(data);
      }
    }, 300),
    [],
  );

  const handleOpen = () => {
    if (openAtFirstClick) {
      if (searchAtFirstClick) {
        setOptions(defaultOptions);
      }
      setOpen(true);
    }
  };

  React.useEffect(() => {
    async function fetchDefaultOptions() {
      if (searchAtFirstClick) {
        const response = await getOptions('');
        const data = response.data;
        if (Array.isArray(data) && isSearchCity) {
          data.unshift(NATION_WIDE as any);
          setDefaultOptions(data);
        } else {
          setDefaultOptions(data);
        }
      }
    }
    fetchDefaultOptions().then();
  }, []);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }
    return () => {
      handleSearch.cancel();
    };
  }, [open]);

  return (
    <div className={className}>
      {(label || subLabel) && (
        <div className="mb-2 tablet:mb-3 flex flex-col">
          {label && (
            <label
              className={`font-semibold ${labelColor} leading-5.5 text-sm`}
            >
              {label}
            </label>
          )}
          {subLabel && (
            <label className="text-xs mt-1 text-gray-600">{subLabel}</label>
          )}
        </div>
      )}
      <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
        freeSolo={freeSolo}
        disabled={disabled}
        blurOnSelect={true}
        clearOnBlur={clearOnBlur}
        options={options}
        value={value}
        inputValue={inputValue}
        open={open}
        noOptionsText={noOptionsText}
        loading={freeSolo ? false : loading}
        loadingText={<CircularProgress color="inherit" size={24} />}
        onOpen={handleOpen}
        onClose={() => setOpen(false)}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        PopperComponent={styledPopper}
        renderInput={(params) => {
          return (
            <Input
              autoComplete="off"
              sx={inputSx}
              inputClassName={inputClassName}
              error={error}
              inputRef={params.InputProps.ref}
              helperText={helperText}
              placeholder={placeholder}
              inputProps={params.inputProps}
              disabled={disabled}
              startAdornment={startAdornment}
              endAdornment={
                <div onClick={() => clearValue && setInputValue('')}>
                  {endAdornment}
                </div>
              }
              disableUnderline={disableUnderline}
            />
          );
        }}
        {...otherProps}
      />
    </div>
  );
};

export default MyAutocomplete;
